/** Hall of Fame → every qualifying player by win rate, 25 per page (`/hof/win-rate`). */

import React from 'react'
import { ScrollView, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { HOF_MAX_WIDTH, HOF_MIN_GAMES, HOF_PAGE_SIZE, winRatePageCount, winRateQuery } from '../../../lib/hallOfFame'
import { pageLayout } from '../../../lib/pageLayout'
import { ScreenHeader } from '../../../components/ui/ScreenHeader'
import { HofSectionCard } from '../../../components/hof/HofSectionCard'
import { WinRateRow } from '../../../components/hof/WinRateRow'

function PageButton({ label, accessibilityLabel, disabled, onPress }: { label: string; accessibilityLabel: string; disabled: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }} className="border border-olive rounded-md px-3 py-2"
      style={{ minHeight: 40, minWidth: 88, alignItems: 'center', justifyContent: 'center', opacity: disabled ? 0.4 : 1 }}>
      <Text className="font-stencil text-[11px] uppercase text-blood">{label}</Text>
    </TouchableOpacity>
  )
}

export default function WinRateScreen() {
  const [page, setPage] = React.useState(0)
  // Keep the current page on screen while the next one loads, so paging does
  // not flash back to the loading state.
  const winRate = useQuery({ ...winRateQuery(HOF_PAGE_SIZE, page * HOF_PAGE_SIZE), placeholderData: keepPreviousData })
  const entries = winRate.data?.entries ?? []
  const pageCount = winRatePageCount(winRate.data?.total)
  const { width } = useWindowDimensions()
  const { inset } = pageLayout(width, { maxWidth: HOF_MAX_WIDTH })
  const scroll = React.useRef<ScrollView>(null)
  const goTo = (next: number) => {
    setPage(Math.min(Math.max(next, 0), pageCount - 1))
    scroll.current?.scrollTo({ y: 0, animated: false })
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <ScreenHeader title="Hall of Fame" icon="trophy" />
      <ScrollView ref={scroll} contentContainerStyle={{ paddingHorizontal: inset, paddingVertical: 12 }}>
        <HofSectionCard
          title="Win rate"
          subtitle={`Ranked games · minimum ${HOF_MIN_GAMES} games`}
          isLoading={winRate.isLoading}
          error={winRate.error}
          onRetry={() => { void winRate.refetch() }}
          testID="hof-win-rate-full"
          footer={pageCount > 1 ? (
            <View testID="hof-win-rate-pager" className="flex-row items-center justify-between gap-3">
              <PageButton label="‹ Prev" accessibilityLabel="Previous page" disabled={page === 0} onPress={() => goTo(page - 1)} />
              <Text className="font-typed text-ink-soft text-xs" accessibilityLiveRegion="polite">
                Page {page + 1} of {pageCount}
              </Text>
              <PageButton label="Next ›" accessibilityLabel="Next page" disabled={page >= pageCount - 1} onPress={() => goTo(page + 1)} />
            </View>
          ) : null}
        >
          {entries.length === 0 ? (
            <Text className="font-typed text-ink-soft italic text-xs">
              // no qualified players yet — finish some games
            </Text>
          ) : (
            <View className="gap-2" style={{ opacity: winRate.isPlaceholderData ? 0.6 : 1 }}>
              {entries.map((entry) => <WinRateRow key={entry.id} entry={entry} />)}
            </View>
          )}
        </HofSectionCard>
      </ScrollView>
    </SafeAreaView>
  )
}
